import type { ErrorBody } from "@studio/server/api-types";
import { LOGIN_PATH } from "../routes/paths";

/** 서버가 돌려준 오류 (`ErrorBody`). JSON이 아닌 응답도 `internal`로 감싼다 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ErrorBody;

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const INTERNAL_BODY: ErrorBody = {
  category: "internal",
  code: "internal_error",
  message: "서버 오류가 발생했습니다",
};

const NETWORK_BODY: ErrorBody = {
  category: "unavailable",
  code: "network_error",
  message: "서버에 연결하지 못했습니다",
};

function isErrorBody(value: unknown): value is ErrorBody {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.category === "string" && typeof v.code === "string" && typeof v.message === "string"
  );
}

async function toApiError(res: Response): Promise<ApiError> {
  try {
    const json: unknown = await res.json();
    if (isErrorBody(json)) return new ApiError(res.status, json);
  } catch {
    // JSON이 아닌 본문은 아래에서 internal로 바꾼다
  }
  return new ApiError(res.status, INTERNAL_BODY);
}

export interface RequestOptions {
  /** 401이어도 로그인 이동 콜백을 부르지 않는다 (로그인 요청 자신) */
  skipUnauthorizedHandler?: boolean;
}

export interface ApiClientOptions {
  /** 401을 받았을 때 부른다. 로그인 화면으로 보내는 일을 맡는다 */
  onUnauthorized: () => void;
  fetch?: typeof fetch;
}

export interface ApiClient {
  get<T>(path: string, query?: Record<string, string | undefined>): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete(path: string): Promise<void>;
  /** multipart 본문을 보낸다 (파일 올리기). content-type은 브라우저가 경계와 함께 정한다 */
  postForm<T>(path: string, form: FormData): Promise<T>;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? ((input, init) => fetch(input, init));

  async function request<T>(
    method: string,
    path: string,
    body: unknown,
    requestOptions: RequestOptions = {},
  ): Promise<T> {
    const init: RequestInit = { method, credentials: "same-origin" };
    if (body instanceof FormData) {
      init.body = body;
    } else if (body !== undefined) {
      init.headers = { "content-type": "application/json" };
      init.body = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await doFetch(path, init);
    } catch {
      throw new ApiError(0, NETWORK_BODY);
    }
    if (!res.ok) {
      const error = await toApiError(res);
      if (res.status === 401 && requestOptions.skipUnauthorizedHandler !== true) {
        options.onUnauthorized();
      }
      throw error;
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    get(path, query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== undefined && value !== "") params.set(key, value);
      }
      const qs = params.toString();
      return request("GET", qs === "" ? path : `${path}?${qs}`, undefined);
    },
    post: (path, body, requestOptions) => request("POST", path, body, requestOptions),
    put: (path, body) => request("PUT", path, body),
    patch: (path, body) => request("PATCH", path, body),
    delete: (path) => request<void>("DELETE", path, undefined),
    postForm: (path, form) => request("POST", path, form),
  };
}

function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  if (pathname === LOGIN_PATH) return;
  window.location.assign(`${LOGIN_PATH}?next=${encodeURIComponent(pathname + search)}`);
}

/** 화면에서 쓰는 기본 클라이언트 */
export const api = createApiClient({ onUnauthorized: redirectToLogin });

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "알 수 없는 오류가 발생했습니다";
}
